import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import TopicCard from "@/components/feature/TopicCard";
import { topics } from "@/mocks/topics";

export default function TopicDetail() {
  const { id } = useParams();
  const topic = topics.find((t) => t.id === id);
  const [showAll, setShowAll] = useState(false);

  if (!topic) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 pt-16 md:pt-[72px] flex items-center justify-center px-4">
          <div className="text-center py-24">
            <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-500 mb-5">
              <i className="ri-file-unknow-line text-3xl"></i>
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">这个合集不存在或已下线</h1>
            <p className="mt-3 text-sm text-foreground-600">它可能已经被合并到其他话题，去看看其它合集吧。</p>
            <Link
              to="/collections"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回事件合集
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = topics.filter((t) => topic.relatedIds.includes(t.id));
  const displayTimeline = showAll ? topic.timeline : topic.timeline.slice(-4);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: topic.title,
    description: topic.summary,
    articleSection: topic.category,
    keywords: topic.tags.join(","),
    image: topic.cover,
    dateModified: "2026-09-10",
    author: { "@type": "Organization", name: "蝌蚪黑料" },
    publisher: { "@type": "Organization", name: "蝌蚪黑料" },
    mainEntityOfPage: `https://bhmbk.cn/topic/${topic.id}`,
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <Navbar />
      <main className="flex-1 pt-16 md:pt-[72px]">
        <section className="w-full bg-background-100 border-b border-background-200">
          <div className="max-w-content mx-auto w-full px-4 md:px-8 py-8 md:py-10">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "事件合集", to: "/collections" },
                { label: topic.category, to: `/collections?category=${encodeURIComponent(topic.category)}` },
                { label: topic.title },
              ]}
            />
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs">
                    {topic.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent-100 text-accent-900 text-xs">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse"></span>
                    {topic.status}
                  </span>
                  <span className="text-xs text-foreground-500">最近更新 {topic.lastUpdate}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight">
                  {topic.title}
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  {topic.summary}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {topic.tags.map((t) => (
                    <span key={t} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs">
                      #{t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-4">
                <div className="w-full aspect-[4/3] rounded-lg overflow-hidden">
                  <img
                    src={topic.cover}
                    alt={`${topic.title}事件合集配图`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full">
          <div className="max-w-content mx-auto w-full px-4 md:px-8 py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
              <div className="lg:col-span-8">
                <article>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2">
                    <span className="w-1 h-6 bg-primary-500 rounded-full"></span>
                    瓜料精华
                  </h2>
                  <ul className="mt-5 space-y-4">
                    {topic.highlights.map((h) => (
                      <li key={h} className="flex gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                        <i className="ri-sparkling-2-fill text-accent-500 text-lg shrink-0 mt-0.5"></i>
                        <p className="text-sm md:text-base text-foreground-800 leading-relaxed">{h}</p>
                      </li>
                    ))}
                  </ul>

                  <h2 className="mt-12 font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2">
                    <span className="w-1 h-6 bg-primary-500 rounded-full"></span>
                    事件时间线
                  </h2>
                  <div className="mt-6 relative pl-6">
                    <div className="absolute left-[5px] top-2 bottom-2 w-px bg-background-300"></div>
                    <ul className="space-y-6">
                      {displayTimeline.map((item) => (
                        <li key={item.date + item.title} className="relative">
                          <span className="absolute -left-6 top-1.5 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50"></span>
                          <div className="flex items-center gap-3">
                            <span className="font-heading text-sm font-bold text-primary-600">{item.date}</span>
                            <span className="text-sm md:text-base font-medium text-foreground-900">
                              {item.title}
                            </span>
                          </div>
                          <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{item.detail}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {topic.timeline.length > 4 && (
                    <button
                      onClick={() => setShowAll((v) => !v)}
                      className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-300 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors"
                    >
                      <i className={showAll ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                      {showAll ? "收起早期进展" : `查看全部 ${topic.timeline.length} 条进展`}
                    </button>
                  )}
                </article>

                <div className="mt-10 p-4 rounded-lg bg-secondary-50 border border-secondary-200 text-xs md:text-sm text-secondary-800 leading-relaxed">
                  <i className="ri-information-line mr-1.5"></i>
                  本合集为公开信息整理，旨在梳理话题脉络，不代表对任何一方的定性。涉及具体争议，请以当事人、平台或官方渠道的正式说明为准。
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="sticky top-24 space-y-6">
                  <div className="p-5 rounded-lg bg-background-100 border border-background-200">
                    <h3 className="font-heading text-base font-bold text-foreground-950">合集信息</h3>
                    <dl className="mt-4 space-y-3 text-sm">
                      <div className="flex items-center justify-between">
                        <dt className="text-foreground-500">所属板块</dt>
                        <dd className="text-foreground-900">{topic.category}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-foreground-500">当前状态</dt>
                        <dd className="text-accent-700">{topic.status}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-foreground-500">累计更新</dt>
                        <dd className="text-foreground-900">{topic.updateCount} 次</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-foreground-500">话题热度</dt>
                        <dd className="text-primary-600">{(topic.heat / 10000).toFixed(1)}万</dd>
                      </div>
                    </dl>
                    <Link
                      to="/collections"
                      className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
                    >
                      <i className="ri-compasses-2-line"></i>
                      浏览更多合集
                    </Link>
                  </div>

                  <div className="p-5 rounded-lg bg-background-100 border border-background-200">
                    <h3 className="font-heading text-base font-bold text-foreground-950">关于追更</h3>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                      带「持续更新中」的合集会在出现新的公开进展时补充到时间线，并在顶部刷新更新时间。收藏本页即可随时回看新增内容。
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="w-full bg-background-100 border-t border-background-200">
            <div className="max-w-content mx-auto w-full px-4 md:px-8 py-12 md:py-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-7">
                相关合集推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {related.slice(0, 3).map((t) => (
                  <TopicCard key={t.id} topic={t} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}